import React, { useMemo } from 'react';
import { StaffMember, AttendanceRecord, Shift } from '../types';
import { Icons } from './ui/Icons';
import { calculateMinutesDiff } from '../utils/dateUtils';

interface PayrollReportPrintPageProps {
    periodStart: string;
    periodEnd: string;
    records: AttendanceRecord[];
    staffList: StaffMember[];
    shifts: Shift[];
    propertyName: string;
    onDone: () => void;
}

const PayrollReportPrintPage: React.FC<PayrollReportPrintPageProps> = ({
    periodStart,
    periodEnd,
    records,
    staffList,
    shifts,
    propertyName,
    onDone
}) => {
    // Filter records by date range and status (APPROVED or AUTO_APPROVED)
    const filteredRecords = useMemo(() => {
        return records.filter(r => 
            r.date >= periodStart && 
            r.date <= periodEnd && 
            (r.status === 'APPROVED' || r.status === 'AUTO_APPROVED')
        );
    }, [records, periodStart, periodEnd]);

    // Group records by employeeId
    const employeeData = useMemo(() => {
        const grouped: { [staffId: string]: { 
            staff: StaffMember, 
            records: (AttendanceRecord & { 
                shiftName: string, 
                lateMinutes: number, 
                earlyLeaveMinutes: number,
                isLate: boolean,
                isOvertime: boolean
            })[],
            totalHours: number,
            totalExtraHours: number
        } } = {};

        filteredRecords.forEach(record => {
            if (!grouped[record.employeeId]) {
                const staff = staffList.find(s => s.id === record.employeeId);
                if (!staff) return;
                grouped[record.employeeId] = {
                    staff,
                    records: [],
                    totalHours: 0,
                    totalExtraHours: 0
                };
            }

            const shift = shifts.find(s => s.id === record.shiftId);
            
            // Calculate late/early
            const lateMinutes = Math.max(0, calculateMinutesDiff(record.actualStartTime, record.scheduledStartTime));
            const earlyLeaveMinutes = Math.max(0, calculateMinutesDiff(record.scheduledEndTime, record.actualEndTime));

            grouped[record.employeeId].records.push({
                ...record,
                shiftName: shift?.name || 'Unknown',
                lateMinutes,
                earlyLeaveMinutes,
                isLate: lateMinutes > 5, // Flag if more than 5 mins late
                isOvertime: record.extraHours > 0
            });

            grouped[record.employeeId].totalHours += record.totalHours;
            grouped[record.employeeId].totalExtraHours += record.extraHours;
        });

        // Sort records by date for each employee
        Object.values(grouped).forEach(data => {
            data.records.sort((a, b) => a.date.localeCompare(b.date));
        });

        return Object.values(grouped).sort((a, b) => a.staff.name.localeCompare(b.staff.name));
    }, [filteredRecords, staffList, shifts]);

    const handlePrint = () => {
        window.print();
    };

    if (employeeData.length === 0) {
        return (
            <div className="min-h-screen bg-white p-8 flex flex-col items-center justify-center">
                <p className="text-xl font-bold text-gray-500 mb-4">No attendance data found for this period.</p>
                <button 
                    onClick={onDone}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold"
                >
                    Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white p-8 print:p-0 font-sans text-gray-900">
            {/* Controls - Hidden in print */}
            <div className="max-w-5xl mx-auto mb-8 flex justify-between items-center print:hidden">
                <button 
                    onClick={onDone}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-bold"
                >
                    <Icons.ArrowLeft className="w-4 h-4" />
                    Back to Portal
                </button>
                <button 
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-md"
                >
                    <Icons.Printer className="w-4 h-4" />
                    Print Payroll Report
                </button>
            </div>

            <div className="max-w-5xl mx-auto">
                {/* Multi-Employee Summary Table */}
                <div className="mb-12 p-8 border-2 border-gray-900 rounded-xl bg-gray-50">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h1 className="text-3xl font-black uppercase tracking-tighter text-gray-900">Payroll Period Summary</h1>
                            <p className="text-lg font-bold text-indigo-600">Period: {periodStart} to {periodEnd}</p>
                        </div>
                        <div className="text-right">
                            <p className="font-black uppercase text-xs text-gray-400 tracking-widest">Company Name</p>
                            <p className="font-bold text-gray-900">{propertyName}</p>
                        </div>
                    </div>

                    <h2 className="text-xl font-bold uppercase tracking-wider mb-4 border-b-2 border-gray-900 pb-2">Employee Totals</h2>
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-gray-300">
                                <th className="py-3 font-black uppercase text-xs">Employee Name</th>
                                <th className="py-3 font-black uppercase text-xs text-right">Total Hours</th>
                                <th className="py-3 font-black uppercase text-xs text-right">Extra Hours</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {employeeData.map((data, idx) => (
                                <tr key={idx}>
                                    <td className="py-3 font-bold">{data.staff.name}</td>
                                    <td className="py-3 text-right font-mono font-bold">{data.totalHours.toFixed(2)}h</td>
                                    <td className="py-3 text-right font-mono">
                                        <span className={data.totalExtraHours > 0 ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
                                            {data.totalExtraHours > 0 ? `+${data.totalExtraHours.toFixed(2)}` : '0.00'}h
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-900 font-black">
                                <td className="py-4 text-right uppercase">Grand Totals</td>
                                <td className="py-4 text-right text-indigo-600">
                                    {employeeData.reduce((sum, d) => sum + d.totalHours, 0).toFixed(2)}h
                                </td>
                                <td className="py-4 text-right text-emerald-600">
                                    {employeeData.reduce((sum, d) => sum + d.totalExtraHours, 0).toFixed(2)}h
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Individual Employee Sheets - Page Break per Employee */}
                {employeeData.map((data, idx) => (
                    <div key={idx} className="page-break-before mb-16 pt-8 border-t-4 border-double border-gray-200 print:border-0 print:pt-0">
                        <div className="flex justify-between items-end mb-6">
                            <div>
                                <p className="text-xs font-black uppercase text-gray-400 tracking-widest mb-1">Employee Timesheet</p>
                                <h2 className="text-4xl font-black uppercase tracking-tighter text-gray-900">{data.staff.name}</h2>
                                <p className="text-sm font-bold text-gray-500">ID: {data.staff.id} • Role: {data.staff.role}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm font-bold text-gray-900">Period: {periodStart} → {periodEnd}</p>
                            </div>
                        </div>

                        <table className="w-full text-left border-collapse mb-8">
                            <thead>
                                <tr className="border-y-2 border-gray-900 bg-gray-50">
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider">Date</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider">Shift</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider">Scheduled</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider">Actual</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider text-right">Hours</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider text-right">Extra</th>
                                    <th className="px-4 py-3 font-black uppercase text-[10px] tracking-wider">Notes</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.records.map((record, rIdx) => (
                                    <tr key={rIdx} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 text-sm font-bold">{record.date}</td>
                                        <td className="px-4 py-3 text-xs font-medium text-gray-600 uppercase">{record.shiftName}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                                            {record.scheduledStartTime} - {record.scheduledEndTime}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-bold font-mono">
                                            {record.actualStartTime} - {record.actualEndTime}
                                            {record.isLate && (
                                                <span className="ml-2 text-[8px] bg-rose-100 text-rose-700 px-1 rounded uppercase font-black">Late</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono font-bold">{record.totalHours.toFixed(2)}</td>
                                        <td className="px-4 py-3 text-right font-mono">
                                            <span className={record.extraHours > 0 ? 'text-emerald-600 font-bold' : 'text-gray-300'}>
                                                {record.extraHours > 0 ? `+${record.extraHours.toFixed(2)}` : '0.00'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-[10px] text-gray-400 italic">
                                            {record.isLate ? `${record.lateMinutes}m late` : ''}
                                            {record.earlyLeaveMinutes > 0 ? `${record.isLate ? ', ' : ''}${record.earlyLeaveMinutes}m early leave` : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-gray-900 bg-gray-50">
                                    <td colSpan={4} className="px-4 py-4 text-right font-black uppercase text-xs">Employee Totals</td>
                                    <td className="px-4 py-4 text-right font-black text-lg text-indigo-900 font-mono">{data.totalHours.toFixed(2)}h</td>
                                    <td className="px-4 py-4 text-right font-black text-lg text-emerald-600 font-mono">+{data.totalExtraHours.toFixed(2)}h</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>

                        <div className="grid grid-cols-2 gap-12 mt-12">
                            <div className="border-t border-gray-300 pt-4">
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-8">Employee Signature</p>
                                <div className="h-12 border-b border-gray-200"></div>
                            </div>
                            <div className="border-t border-gray-300 pt-4">
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-8">Admin Signature</p>
                                <div className="h-12 border-b border-gray-200"></div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="mt-16 pt-8 border-t border-gray-200 text-center text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    Generated on {new Date().toLocaleString()} • Payroll Period Summary • {propertyName}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    .page-break-before {
                        page-break-before: always;
                    }
                    body {
                        background: white;
                    }
                    @page {
                        size: A4;
                        margin: 20mm;
                    }
                }
            `}} />
        </div>
    );
};

export default PayrollReportPrintPage;
